import styled from '@emotion/styled';
import Layout from '@ep/components/Layout';
import SEO from '@ep/components/Seo';
import FlexBannerModule, {
  FlexBannerModuleProps,
} from '@ep/modules/FlexBannerModule';
import HeroCTAModule, { HeroCTAModuleProps } from '@ep/modules/HeroCTAModule';
import SectionMenu, { SectionMenuProps } from '@ep/modules/SectionMenuModule';
import SocialWallModule from '@ep/modules/SocialWallModule';
import VerticalThumbnailsModule from '@ep/modules/VerticalThumbnailsModule';
import { Gcms_InteractiveCard, Gcms_Post } from '@ep/types/gatsbyTypes';
import { Gcms_EpNowPage } from '@ep/types/gatsbyTypes';
import getModuleProps from '@ep/utils/getModuleProps';
import getSeoProps from '@ep/utils/getSeoProps';
import { WindowLocation } from '@reach/router';
import { graphql } from 'gatsby';
import React from 'react';

export interface EpNowPageProps {
  location: WindowLocation;
  data: {
    gcms: {
      epNowPage: Gcms_EpNowPage;
      masterSeriesPosts: Gcms_Post[];
      blogPosts: Gcms_Post[];
      inTheNewsPosts: Gcms_Post[];
      pressReleasePosts: Gcms_Post[];
      legalAndCompliancePosts: Gcms_Post[];
      industryNewsPosts: Gcms_InteractiveCard[];
    };
  };
}

export const Head = ({ data }: Pick<EpNowPageProps, 'data'>) => {
  const page = data.gcms.epNowPage;
  return <SEO {...getSeoProps(page)} />;
};

const Grid = styled.div`
  display: flex;
  margin-top: 0rem;
  margin-bottom: 3rem;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
`;

function EpNowPage({ location, data }: EpNowPageProps) {
  const page = data.gcms.epNowPage;
  const blogPosts = data.gcms.blogPosts;
  const inTheNewsPosts = data.gcms.inTheNewsPosts;
  const pressReleasePosts = data.gcms.pressReleasePosts;
  const industryNewsPosts = data.gcms.industryNewsPosts;
  const hasSectionMenu = Boolean(page.subMenu.length != 0);

  return (
    <Layout pathname={location.pathname}>
      {hasSectionMenu && (
        <SectionMenu
          pathname={location.pathname}
          {...(getModuleProps(page, 'sub') as SectionMenuProps)}
        />
      )}
      <HeroCTAModule
        {...(getModuleProps(page, 'hero') as HeroCTAModuleProps)}
      />
      {page.flexBannerDisplay && (
        <FlexBannerModule
          {...(getModuleProps(page, 'flexBanner') as FlexBannerModuleProps)}
        />
      )}
      <Grid>
        <VerticalThumbnailsModule
          icon="IconBlogBlue"
          label="Blog"
          link="/spotlight/blog/"
          posts={blogPosts}
        />
        <VerticalThumbnailsModule
          icon="IconIndustryNewsBlue"
          label="Industry News"
          link="/spotlight/industry-news/"
          posts={industryNewsPosts}
        />
         <VerticalThumbnailsModule
          icon="IconNewsroomBlue"
          label="100 Years"
          //link="/spotlight/newsroom/"
          link="/spotlight/central-100/"
          posts={inTheNewsPosts}
          additionalPosts={pressReleasePosts}
        />
      </Grid>
      <SocialWallModule />
    </Layout>
  );
}

export const query = graphql`
  query EpNowPageQuery($id: ID!) {
    gcms {
      epNowPage(where: { id: $id }) {
        title
        description
        structuredData
        slug
        featuredImage {
          url
          altText
        }
        metaTitle
        metaDecsription
        metaDescription
        subMenu {
          data
        }
        heroHeading
        heroImage {
          url
          altText
          vidyardLink
          options
        }
        heroDescription
        heroRight
        heroLabel
        heroIcon {
          url
          altText
        }
        heroSubheading {
          html
        }
        heroButtonLabel
        heroButtonLink
        heroBackground {
          url
          altText
          vidyardLink
        }
        flexBannerDisplay
        flexBannerBackgroundColor {
          hex
        }
        flexBannerTextColor {
          hex
        }
        flexBannerContent {
          html
        }
        flexBannerExpirationDateTime
        flexBannerShow
        expertsBannerHeading
        expertsBannerDescription
        expertsBannerButton
        expertsBannerButtonLink
        expertsBannerBottomText {
          html
        }
        expertsBannerProfiles {
          expertBannerImage {
            url
            altText
            vidyardLink
          }
          expertsBannerDetails {
            html
          }
          imageLink
        }
      }
      blogPosts: posts(
        where: { postType: Blog }
        orderBy: displayDate_DESC
        first: 4
      ) {
        id
        updatedAt
        title
        description
        slug
        postType
        postTopic
        postTags
        displayDate
        featuredImage {
          url
          altText
          vidyardLink
        }
      }
      inTheNewsPosts: posts(
        where: { postType: In_The_News }
        orderBy: displayDate_DESC
        first: 4
      ) {
        id
        updatedAt
        title
        description
        slug
        postType
        postTopic
        postTags
        displayDate
        featuredImage {
          url
          altText
          vidyardLink
        }
      }
      pressReleasePosts: posts(
        where: { postType: Press_Release }
        orderBy: displayDate_DESC
        first: 4
      ) {
        id
        updatedAt
        title
        description
        slug
        postType
        postTopic
        postTags
        displayDate
        featuredImage {
          url
          altText
          vidyardLink
        }
      }
      industryNewsPosts: posts(
        where: { postType: Industry_News }
        orderBy: displayDate_DESC
        first: 4
      ) {
        id
        updatedAt
        title
        description
        slug
        postType
        postTopic
        postTags
        displayDate
        featuredImage {
          url
          altText
          vidyardLink
        }
      }
    }
  }
`;

export default EpNowPage;
